import { Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import {
  DataTableEndlessPaginated,
  EmptyStateProps,
} from '@/components/data-table/data-table-endless-paginated';
import { StickyTableStateContent } from '@/components/data-table/data-table-states';

import { TIME_OFF_APPROVALS_TABLE_ID } from '../../constants/time-off-approvals-filter-keys';
import { TimeOffRequest } from '../../types/time-off-request';
import { TimeOffTimeCategory } from '../../types/time-off-time-category';

interface TimeOffApprovalsTableProps {
  table: Table<TimeOffRequest>;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  error?: string;
  onResizeEnd: () => void;
  hasActiveFilters: boolean;
  /** Opens the request in the details page (approve mode). */
  onRowClick?: (request: TimeOffRequest) => void;
  getRowHref?: (request: TimeOffRequest) => string | undefined;
}

export const TimeOffApprovalsTable = ({
  table,
  isLoading,
  hasMore,
  onLoadMore,
  error,
  onResizeEnd,
  hasActiveFilters,
  onRowClick,
  getRowHref,
}: TimeOffApprovalsTableProps) => {
  const { t } = useTranslation(['authenticated.timeOff', 'common']);

  const renderEmptyState = ({ colSpan }: EmptyStateProps) => {
    if (hasActiveFilters) {
      return (
        <tr>
          <td colSpan={colSpan} className="h-16 w-full px-0">
            <StickyTableStateContent className="px-12 py-4">
              <div className="text-sm">{t('common:noResultsFound')}</div>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    return (
      <tr>
        <td colSpan={colSpan} className="w-full px-0 py-4">
          <StickyTableStateContent className="items-start gap-1 px-8 text-left">
            <div className="flex flex-col gap-0.5">
              <div className="text-sm font-semibold text-foreground">
                {t('approvals.table.emptyTitle')}
              </div>
              <div className="text-xs text-muted-foreground">
                {t('approvals.table.emptyDescription')}
              </div>
            </div>
          </StickyTableStateContent>
        </td>
      </tr>
    );
  };

  return (
    <DataTableEndlessPaginated
      table={table}
      isLoading={isLoading}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      error={error}
      tableId={TIME_OFF_APPROVALS_TABLE_ID}
      onResizeEnd={onResizeEnd}
      onRowClick={onRowClick}
      getRowHref={getRowHref}
      renderEmptyState={renderEmptyState}
      stickyHeader
      fixedHeight
      getRowClassName={row =>
        // Opaque tint (not bg-muted/30) so the pinned Employee cell doesn't let
        // scrolled-under columns bleed through. color-mix keeps it theme-aware.
        row.timeCategory === TimeOffTimeCategory.Past
          ? 'bg-[color-mix(in_srgb,hsl(var(--muted))_30%,hsl(var(--background)))]'
          : undefined
      }
    />
  );
};
